import { useMemo, useState } from 'react';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import { pontosParaAvaliacao, PONTOS_HISTORICO, type PontosEscalasConfig, type AvaliacaoSiadap } from '@/utils/progressaoUtils';

type ViewType = 'media' | 'distribuicao' | 'avaliacao';

const CARREIRAS = ['Técnica Superior', 'Assistente Técnico', 'Assistente Operacional'] as const;
const CARREIRA_COLORS: Record<string, { bar: string; bg: string; text: string; light: string }> = {
  'Técnica Superior':       { bar: '#10b981', bg: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-50' },
  'Assistente Técnico':     { bar: '#f59e0b', bg: 'bg-amber-500',   text: 'text-amber-700',   light: 'bg-amber-50'   },
  'Assistente Operacional': { bar: '#6366f1', bg: 'bg-indigo-500',  text: 'text-indigo-700',  light: 'bg-indigo-50'  },
};

const AVALIACAO_COLORS: Record<string, string> = {
  Excelente:   '#10b981',
  'Muito Bom': '#f59e0b',
  Bom:         '#94a3b8',
  Regular:     '#f97316',
  Inadequado:  '#ef4444',
  Relevante:   '#14b8a6',
  Adequado:    '#94a3b8',
};

function anoFinalDoPeriodo(periodo: string): number {
  const partes = (periodo ?? '').trim().split('-');
  const ultimo = partes[partes.length - 1];
  const ano = parseInt(ultimo, 10);
  return isNaN(ano) ? 9999 : ano;
}

export default function EvolucaoPontosChart() {
  const { funcionarios } = useFuncionarios();
  const { registosSiadap, registosHistorico } = useSiadap();
  const { settings } = useAppSettings();
  const [viewType, setViewType] = useState<ViewType>('media');
  const [carreiraFilter, setCarreiraFilter] = useState<string>('todas');

  const escalas = useMemo<PontosEscalasConfig>(() => ({
    pos2025: {
      Excelente:   settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom:         settings.pontos_bom,
      Regular:     settings.pontos_regular,
      Inadequado:  settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente:   settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom:         settings.pontos_bom_2023,
      Regular:     settings.pontos_regular_2023,
      Inadequado:  settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente:   settings.pontos_excelente_pre2023,
      Relevante:   settings.pontos_relevante_pre2023,
      Adequado:    settings.pontos_adequado_pre2023,
      Inadequado:  settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom:         settings.pontos_adequado_pre2023,
      Regular:     settings.pontos_adequado_pre2023,
    },
  }), [settings]);

  const pontosMap = useMemo<Partial<Record<AvaliacaoSiadap, number>>>(() => ({
    Excelente:   settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom:         settings.pontos_bom,
    Regular:     settings.pontos_regular,
    Inadequado:  settings.pontos_inadequado,
  }), [settings]);

  // Todos os períodos/anos únicos ordenados
  const periodos = useMemo(() => {
    const anos = new Set<number>();
    registosSiadap.forEach((r) => {
      const ano = anoFinalDoPeriodo(r.periodoAvaliacao ?? '');
      if (ano < 9999) anos.add(ano);
    });
    registosHistorico.forEach((r) => {
      if (r.ano) anos.add(r.ano);
    });
    return Array.from(anos).sort((a, b) => a - b);
  }, [registosSiadap, registosHistorico]);

  // Mapa funcionário → carreira
  const funcCarreira = useMemo(() => {
    const m: Record<string, string> = {};
    funcionarios.forEach((f) => { m[f.id] = f.carreira; });
    return m;
  }, [funcionarios]);

  // Dados de média de pontos por carreira por período
  const dadosMediaPorCarreira = useMemo(() => {
    return CARREIRAS.map((carreira) => {
      const pontosPorPeriodo = periodos.map((ano) => {
        const registosSiadapAno = registosSiadap.filter((r) => {
          const rAno = anoFinalDoPeriodo(r.periodoAvaliacao ?? '');
          return rAno === ano && funcCarreira[r.funcionarioId] === carreira;
        });
        const registosHistoricoAno = registosHistorico.filter((r) => {
          return r.ano === ano && funcCarreira[r.funcionarioId] === carreira;
        });

        const pontosSiadap = registosSiadapAno.map((r) =>
          pontosParaAvaliacao(r.avaliacao, r.periodoAvaliacao ?? '', pontosMap, escalas)
        );
        const pontosHist = registosHistoricoAno.map((r) =>
          PONTOS_HISTORICO[r.classificacaoServico] ?? 0
        );
        const todos = [...pontosSiadap, ...pontosHist];
        const media = todos.length > 0 ? todos.reduce((s, v) => s + v, 0) / todos.length : null;
        return { ano, media, count: todos.length };
      });
      return { carreira, pontosPorPeriodo };
    });
  }, [periodos, registosSiadap, registosHistorico, funcCarreira, pontosMap, escalas]);

  // Dados de distribuição de avaliações por período
  const dadosDistribuicao = useMemo(() => {
    return periodos.map((ano) => {
      const filtrados = carreiraFilter === 'todas'
        ? registosSiadap.filter((r) => anoFinalDoPeriodo(r.periodoAvaliacao ?? '') === ano)
        : registosSiadap.filter((r) => anoFinalDoPeriodo(r.periodoAvaliacao ?? '') === ano && funcCarreira[r.funcionarioId] === carreiraFilter);

      const contagem: Record<string, number> = {};
      filtrados.forEach((r) => {
        contagem[r.avaliacao] = (contagem[r.avaliacao] ?? 0) + 1;
      });
      return { ano, contagem, total: filtrados.length };
    }).filter((d) => d.total > 0);
  }, [periodos, registosSiadap, funcCarreira, carreiraFilter]);

  // Dados de avaliações por carreira (totais acumulados)
  const dadosAvaliacaoPorCarreira = useMemo(() => {
    return CARREIRAS.map((carreira) => {
      const registos = registosSiadap.filter((r) => funcCarreira[r.funcionarioId] === carreira);
      const contagem: Record<string, number> = {};
      registos.forEach((r) => {
        contagem[r.avaliacao] = (contagem[r.avaliacao] ?? 0) + 1;
      });
      const total = registos.length;
      return { carreira, contagem, total };
    });
  }, [registosSiadap, funcCarreira]);

  // Valor máximo para escala do gráfico de médias
  const maxMedia = useMemo(() => {
    let max = 0;
    dadosMediaPorCarreira.forEach((d) => {
      d.pontosPorPeriodo.forEach((p) => {
        if (p.media !== null && p.media > max) max = p.media;
      });
    });
    return Math.ceil(max) || 6;
  }, [dadosMediaPorCarreira]);

  const carreirasFiltradas = carreiraFilter === 'todas'
    ? CARREIRAS
    : CARREIRAS.filter((c) => c === carreiraFilter);

  if (periodos.length === 0) return null;

  const CHART_H = 160;
  const CHART_W_PER_PERIOD = 60;
  const chartWidth = Math.max(400, periodos.length * CHART_W_PER_PERIOD + 60);

  return (
    <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-100">
            <i className="ri-line-chart-line text-zinc-600 text-base" />
          </div>
          <div>
            <h3 className="text-[13px] font-bold text-zinc-800">Evolução de Pontos por Ciclo SIADAP</h3>
            <p className="text-[11px] text-zinc-400 mt-0.5">Média de pontos acumulados por carreira ao longo dos ciclos avaliativos</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Toggle de vista */}
          <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
            {([
              { key: 'media', label: 'Média', icon: 'ri-line-chart-line' },
              { key: 'distribuicao', label: 'Distribuição', icon: 'ri-bar-chart-grouped-line' },
              { key: 'avaliacao', label: 'Avaliações', icon: 'ri-pie-chart-line' },
            ] as { key: ViewType; label: string; icon: string }[]).map((v) => (
              <button
                key={v.key}
                onClick={() => setViewType(v.key)}
                className={`whitespace-nowrap flex items-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-md transition-all cursor-pointer ${viewType === v.key ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
              >
                <i className={`${v.icon} text-xs`} />{v.label}
              </button>
            ))}
          </div>
          {/* Filtro de carreira */}
          <select
            value={carreiraFilter}
            onChange={(e) => setCarreiraFilter(e.target.value)}
            className="text-[12px] border border-zinc-200 rounded-lg px-2.5 py-1.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
          >
            <option value="todas">Todas as Carreiras</option>
            {CARREIRAS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="p-6">
        {/* ── VISTA MÉDIA ── */}
        {viewType === 'media' && (
          <div>
            {/* Legenda */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              {(carreiraFilter === 'todas' ? CARREIRAS : [carreiraFilter as typeof CARREIRAS[number]]).map((c) => {
                const cfg = CARREIRA_COLORS[c];
                return (
                  <div key={c} className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full inline-block" style={{ background: cfg.bar }} />
                    <span className="text-[11px] text-zinc-500">{c}</span>
                  </div>
                );
              })}
            </div>

            {/* Gráfico de linhas SVG */}
            <div className="overflow-x-auto">
              <svg width={chartWidth} height={CHART_H + 40} className="block">
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
                  const y = CHART_H - pct * CHART_H + 10;
                  const val = (pct * maxMedia).toFixed(1);
                  return (
                    <g key={pct}>
                      <line x1={40} y1={y} x2={chartWidth - 10} y2={y} stroke="#f1f5f9" strokeWidth={1} />
                      <text x={36} y={y + 4} textAnchor="end" fontSize={9} fill="#94a3b8">{val}</text>
                    </g>
                  );
                })}

                {/* Linhas por carreira */}
                {dadosMediaPorCarreira
                  .filter((d) => carreiraFilter === 'todas' || d.carreira === carreiraFilter)
                  .map((d) => {
                    const cfg = CARREIRA_COLORS[d.carreira];
                    const pontos = d.pontosPorPeriodo.filter((p) => p.media !== null);
                    if (pontos.length < 1) return null;

                    const getX = (idx: number) => 40 + idx * CHART_W_PER_PERIOD + CHART_W_PER_PERIOD / 2;
                    const getY = (media: number) => CHART_H - (media / maxMedia) * CHART_H + 10;

                    // Pontos com índice no array de periodos
                    const pts = d.pontosPorPeriodo
                      .map((p, i) => ({ ...p, x: getX(i), y: p.media !== null ? getY(p.media) : null }))
                      .filter((p) => p.y !== null) as { ano: number; media: number; count: number; x: number; y: number }[];

                    const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

                    return (
                      <g key={d.carreira}>
                        {/* Área preenchida */}
                        {pts.length > 1 && (
                          <path
                            d={`${pathD} L ${pts[pts.length - 1].x} ${CHART_H + 10} L ${pts[0].x} ${CHART_H + 10} Z`}
                            fill={cfg.bar}
                            fillOpacity={0.08}
                          />
                        )}
                        {/* Linha */}
                        {pts.length > 1 && (
                          <path d={pathD} fill="none" stroke={cfg.bar} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        )}
                        {/* Pontos */}
                        {pts.map((p) => (
                          <g key={p.ano}>
                            <circle cx={p.x} cy={p.y} r={4} fill={cfg.bar} stroke="white" strokeWidth={2} />
                            <text x={p.x} y={p.y - 8} textAnchor="middle" fontSize={9} fill={cfg.bar} fontWeight="600">
                              {p.media.toFixed(1)}
                            </text>
                          </g>
                        ))}
                      </g>
                    );
                  })}

                {/* Eixo X — períodos */}
                {periodos.map((ano, i) => (
                  <text key={ano} x={40 + i * CHART_W_PER_PERIOD + CHART_W_PER_PERIOD / 2} y={CHART_H + 28} textAnchor="middle" fontSize={10} fill="#94a3b8">
                    {ano}
                  </text>
                ))}
              </svg>
            </div>
          </div>
        )}

        {/* ── VISTA DISTRIBUIÇÃO ── */}
        {viewType === 'distribuicao' && (
          <div>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {Object.entries(AVALIACAO_COLORS).slice(0, 5).map(([aval, color]) => (
                <div key={aval} className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm inline-block" style={{ background: color }} />
                  <span className="text-[11px] text-zinc-500">{aval}</span>
                </div>
              ))}
            </div>

            {dadosDistribuicao.length === 0 ? (
              <div className="py-10 text-center text-zinc-400 text-[13px]">Sem dados de avaliações SIADAP</div>
            ) : (
              <div className="overflow-x-auto">
                <div className="flex items-end gap-4 min-w-max pb-2">
                  {dadosDistribuicao.map((d) => {
                    const avaliacoes = ['Excelente', 'Muito Bom', 'Bom', 'Regular', 'Inadequado'];
                    return (
                      <div key={d.ano} className="flex flex-col items-center gap-2">
                        {/* Barra empilhada */}
                        <div className="flex flex-col-reverse w-12 rounded-lg overflow-hidden" style={{ height: CHART_H }}>
                          {avaliacoes.map((aval) => {
                            const count = d.contagem[aval] ?? 0;
                            if (count === 0) return null;
                            const pct = (count / d.total) * 100;
                            return (
                              <div
                                key={aval}
                                style={{ height: `${pct}%`, background: AVALIACAO_COLORS[aval] }}
                                title={`${aval}: ${count} (${pct.toFixed(0)}%)`}
                                className="transition-all"
                              />
                            );
                          })}
                        </div>
                        <div className="text-center">
                          <p className="text-[11px] font-semibold text-zinc-700">{d.ano}</p>
                          <p className="text-[10px] text-zinc-400">{d.total} aval.</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── VISTA AVALIAÇÕES POR CARREIRA ── */}
        {viewType === 'avaliacao' && (
          <div className="space-y-4">
            {dadosAvaliacaoPorCarreira
              .filter((d) => carreiraFilter === 'todas' || d.carreira === carreiraFilter)
              .map((d) => {
                const cfg = CARREIRA_COLORS[d.carreira];
                const avaliacoes = ['Excelente', 'Muito Bom', 'Bom', 'Regular', 'Inadequado'];
                if (d.total === 0) return null;
                return (
                  <div key={d.carreira}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: cfg.bar }} />
                        <span className="text-[12px] font-semibold text-zinc-700">{d.carreira}</span>
                      </div>
                      <span className="text-[11px] text-zinc-400">{d.total} avaliações</span>
                    </div>
                    {/* Barra horizontal empilhada */}
                    <div className="h-7 rounded-lg overflow-hidden flex">
                      {avaliacoes.map((aval) => {
                        const count = d.contagem[aval] ?? 0;
                        if (count === 0) return null;
                        const pct = (count / d.total) * 100;
                        return (
                          <div
                            key={aval}
                            style={{ width: `${pct}%`, background: AVALIACAO_COLORS[aval] }}
                            title={`${aval}: ${count} (${pct.toFixed(0)}%)`}
                            className="flex items-center justify-center transition-all"
                          >
                            {pct > 8 && (
                              <span className="text-[10px] font-bold text-white">{pct.toFixed(0)}%</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Legenda inline */}
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      {avaliacoes.map((aval) => {
                        const count = d.contagem[aval] ?? 0;
                        if (count === 0) return null;
                        return (
                          <div key={aval} className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-sm inline-block" style={{ background: AVALIACAO_COLORS[aval] }} />
                            <span className="text-[10px] text-zinc-500">{aval}: <strong className="text-zinc-700">{count}</strong></span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}
